import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { physioApi, blogCategoryApi, getAuthSession, getMediaUrl } from '../services/api';

const CreateBlogPage = () => {
  const authSession = getAuthSession();
  const formRef = useRef(null);

  // Form State matching Admin Blog section exactly
  const [formData, setFormData] = useState({
    title: '',
    author: authSession?.user?.name ? `Dr. ${authSession.user.name}` : '',
    category: 'Mental Health',
    excerpt: '',
    content: '',
    featuredImage: ''
  });

  const [categories, setCategories] = useState([
    'Mental Health',
    'Anxiety & Stress',
    'Depression',
    'Relationships',
    'Self-Care',
    'Mindfulness',
    'Career & Work',
    'Personal Growth'
  ]);

  const [editingId, setEditingId] = useState(null);
  const [myBlogs, setMyBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchCategories = async () => {
    try {
      const res = await blogCategoryApi.getPublicCategories();
      if (res?.categories && Array.isArray(res.categories) && res.categories.length > 0) {
        const names = res.categories.map((c) => c.name);
        setCategories(names);
        if (!formData.category || !names.includes(formData.category)) {
          setFormData((prev) => ({ ...prev, category: names[0] }));
        }
      }
    } catch (err) {
      console.warn('Could not load dynamic blog categories, using defaults:', err);
    }
  };

  const fetchMyBlogs = async () => {
    try {
      setLoadingBlogs(true);
      const res = await physioApi.getBlogs();
      const list = res.blogs || res.data || res || [];
      setMyBlogs(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to fetch therapist blogs:', err);
    } finally {
      setLoadingBlogs(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchMyBlogs();
    if (authSession?.user?.name && !formData.author) {
      setFormData((prev) => ({ ...prev, author: `Dr. ${authSession.user.name}` }));
    }
  }, []);

  // Handle Cover Image Upload
  const handleImageFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      setError('');
      const data = new FormData();
      data.append('file', file);
      const res = await physioApi.uploadBlogImage(data);
      if (res?.url) {
        setFormData((prev) => ({ ...prev, featuredImage: res.url }));
        setSuccessMsg('Cover image uploaded successfully!');
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        throw new Error(res?.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      setError(err.message || 'Image upload failed. You can also paste an image URL directly.');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle form reset / Cancel
  const handleResetForm = () => {
    setEditingId(null);
    setFormData({
      title: '',
      author: authSession?.user?.name ? `Dr. ${authSession.user.name}` : '',
      category: categories[0] || 'Mental Health',
      excerpt: '',
      content: '',
      featuredImage: ''
    });
    setError('');
  };

  // Start Editing an existing blog
  const handleEditClick = (blog) => {
    setEditingId(blog._id || blog.id);
    setFormData({
      title: blog.title || '',
      author: blog.createdBy?.name ? `Dr. ${blog.createdBy.name}` : (authSession?.user?.name ? `Dr. ${authSession.user.name}` : ''),
      category: blog.category || 'Mental Health',
      excerpt: blog.excerpt || '',
      content: blog.content || '',
      featuredImage: blog.featuredImage || blog.image || ''
    });
    setError('');
    setSuccessMsg('');
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Handle Delete Blog
  const handleDeleteClick = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await physioApi.deleteBlog(blogId);
      setMyBlogs((prev) => prev.filter((b) => b._id !== blogId && b.id !== blogId));
      setSuccessMsg('Article deleted successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
      if (editingId === blogId) {
        handleResetForm();
      }
    } catch (err) {
      console.error('Failed to delete blog:', err);
      setError(err.message || 'Failed to delete article.');
    }
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!formData.title || formData.title.trim().length < 3) {
      setError('Title must be at least 3 characters long.');
      return;
    }

    if (!formData.excerpt || formData.excerpt.trim().length < 3) {
      setError('Please provide a brief excerpt or summary (at least 3 characters).');
      return;
    }

    if (!formData.content || formData.content.trim().length < 10) {
      setError('Article content must be at least 10 characters long.');
      return;
    }

    const payload = {
      title: formData.title.trim(),
      category: formData.category || 'Mental Health',
      excerpt: formData.excerpt.trim(),
      content: formData.content.trim(),
      featuredImage: formData.featuredImage?.trim() || ''
    };

    try {
      setLoading(true);

      if (editingId) {
        const res = await physioApi.updateBlog(editingId, payload);
        const updated = res.blog || res;
        setMyBlogs((prev) =>
          prev.map((b) => ((b._id === editingId || b.id === editingId) ? { ...b, ...updated, ...payload } : b))
        );
        setSuccessMsg('Article updated successfully!');
      } else {
        const res = await physioApi.addBlog(payload);
        const newBlog = res.blog || res;
        setMyBlogs((prev) => [newBlog, ...prev]);
        setSuccessMsg('Article submitted successfully! Awaiting admin review and approval.');
      }

      handleResetForm();
      setTimeout(() => setSuccessMsg(''), 5000);
      fetchMyBlogs();
    } catch (err) {
      console.error('Failed to save article:', err);
      setError(err.message || 'Failed to save article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filtered blogs
  const filteredBlogs = myBlogs.filter((b) => {
    if (statusFilter !== 'all' && b.status !== statusFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const title = b.title?.toLowerCase() || '';
      const category = b.category?.toLowerCase() || '';
      const excerpt = b.excerpt?.toLowerCase() || '';
      return title.includes(q) || category.includes(q) || excerpt.includes(q);
    }
    return true;
  });

  return (
    <>
      <DashboardSidebar userRole="physiotherapist" />
      <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '40px 24px', marginLeft: '280px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          
          {/* Top Breadcrumb & Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '15px' }}>
            <div>
              <Link to="/physio-dashboard" style={{ color: '#6366f1', textDecoration: 'none', marginBottom: '8px', display: 'inline-block', fontWeight: 600, fontSize: '13.5px' }}>
                ← Back to Therapist Dashboard
              </Link>
              <h1 style={{ fontSize: '28px', color: '#0f172a', margin: '4px 0 6px 0', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px' }}>
                ✍️ {editingId ? 'Edit Blog Article' : 'Write New Blog Article'}
              </h1>
              <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>
                Share your clinical expertise, therapeutic insights, and mental wellness guides with the community.
              </p>
            </div>

            {editingId && (
              <button
                onClick={handleResetForm}
                style={{
                  padding: '8px 16px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                ➕ Switch to New Article
              </button>
            )}
          </div>

          {/* Feedback Messages */}
          {error && (
            <div style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '14px 18px', borderRadius: '10px', marginBottom: '20px', fontWeight: 600, fontSize: '14px' }}>
              ❌ {error}
            </div>
          )}

          {successMsg && (
            <div style={{ background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', padding: '14px 18px', borderRadius: '10px', marginBottom: '20px', fontWeight: 600, fontSize: '14px' }}>
              ✅ {successMsg}
            </div>
          )}

          {/* ==================== BLOG FORM MATCHING ADMIN SECTION ==================== */}
          <div
            ref={formRef}
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              border: editingId ? '2px solid #6366f1' : '1px solid #e2e8f0',
              marginBottom: '40px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                {editingId ? '✏️ Modify Article Details' : '📝 Article Details'}
              </h3>
              {editingId && (
                <span style={{ fontSize: '12px', background: '#ede9fe', color: '#6d28d9', padding: '4px 10px', borderRadius: '20px', fontWeight: 700 }}>
                  Editing Mode Active
                </span>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              {/* Blog Title */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                  Blog Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter blog article title (e.g. 5 Grounding Techniques for Anxiety)"
                  required
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
              </div>

              {/* 2-Columns: Author Name & Category */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px', marginBottom: '18px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                    Author Name
                  </label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    placeholder="Author name (e.g. Dr. John Doe)"
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      background: '#fff',
                      outline: 'none'
                    }}
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Cover Image URL & File Upload */}
              <div style={{ marginBottom: '18px', background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                  Cover Image
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <input
                    type="text"
                    value={formData.featuredImage}
                    onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                    placeholder="https://images.unsplash.com/... or upload image"
                    style={{
                      flex: 1,
                      minWidth: '240px',
                      padding: '10px 14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      background: '#fff',
                      outline: 'none'
                    }}
                  />

                  <label
                    style={{
                      padding: '10px 16px',
                      background: '#ede9fe',
                      color: '#6d28d9',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: uploadingImage ? 'not-allowed' : 'pointer',
                      border: '1px solid #ddd6fe',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileUpload}
                      disabled={uploadingImage}
                      style={{ display: 'none' }}
                    />
                    {uploadingImage ? '⏳ Uploading...' : '📁 Upload Image File'}
                  </label>

                  {formData.featuredImage && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, featuredImage: '' })}
                      style={{
                        padding: '10px 14px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      ✕ Clear
                    </button>
                  )}
                </div>

                {/* Instant Cover Preview */}
                {formData.featuredImage && (
                  <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img
                      src={getMediaUrl(formData.featuredImage)}
                      alt="Cover Preview"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      style={{
                        width: '90px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1'
                      }}
                    />
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      Image preview loaded successfully.
                    </span>
                  </div>
                )}
              </div>

              {/* Excerpt / Brief Summary */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                  Excerpt / Brief Summary *
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  placeholder="Brief summary displayed on article cards (e.g. A clinical overview of common anxiety symptoms and 5 fast-acting mindfulness steps)..."
                  required
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    outline: 'none',
                    lineHeight: '1.5'
                  }}
                />
              </div>

              {/* Full Article Content */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                  Full Article Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Write your complete therapeutic guide, clinical insights, and step-by-step strategies here..."
                  required
                  rows="8"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    outline: 'none',
                    minHeight: '220px',
                    lineHeight: '1.6'
                  }}
                />
              </div>

              {/* Form Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', alignItems: 'center' }}>
                {editingId && (
                  <button
                    type="button"
                    onClick={handleResetForm}
                    style={{
                      padding: '12px 24px',
                      background: '#f1f5f9',
                      color: '#475569',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel Edit
                  </button>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '12px 30px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 700,
                    fontSize: '14px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(99,102,241,0.3)'
                  }}
                >
                  {loading ? 'Submitting...' : editingId ? '💾 Update Article' : '📝 Publish Article'}
                </button>
              </div>
            </form>
          </div>

          {/* ==================== MY ARTICLES LIST SECTION ==================== */}
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '28px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>
                  📚 My Published & Submitted Articles ({myBlogs.length})
                </h3>
                <p style={{ margin: '3px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                  Review the approval status of your articles and edit or manage them anytime.
                </p>
              </div>

              {/* Search & Filter */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Search my articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13px',
                    outline: 'none',
                    width: '180px'
                  }}
                />

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13px',
                    outline: 'none',
                    background: '#fff'
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="approved">✓ Approved / Live</option>
                  <option value="pending">⏳ Pending</option>
                  <option value="rejected">✕ Rejected</option>
                </select>
              </div>
            </div>

            {loadingBlogs ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                ⏳ Loading your articles...
              </div>
            ) : filteredBlogs.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {filteredBlogs.map((blog) => {
                  const blogId = blog._id || blog.id;
                  const isApproved = blog.status === 'approved' || blog.status === 'published';
                  const isRejected = blog.status === 'rejected';

                  const statusBg = isApproved ? '#dcfce7' : isRejected ? '#fee2e2' : '#fef3c7';
                  const statusColor = isApproved ? '#15803d' : isRejected ? '#b91c1c' : '#b45309';
                  const dateStr = blog.createdAt
                    ? new Date(blog.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'Recent';

                  return (
                    <div
                      key={blogId}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        padding: '18px',
                        background: '#f8fafc',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        transition: 'border-color 0.2s',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                          {blog.featuredImage && (
                            <img
                              src={getMediaUrl(blog.featuredImage)}
                              alt={blog.title}
                              onError={(e) => { e.target.style.display = 'none'; }}
                              style={{
                                width: '70px',
                                height: '52px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                flexShrink: 0
                              }}
                            />
                          )}

                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  background: '#e0e7ff',
                                  color: '#4338ca'
                                }}
                              >
                                {blog.category || 'Mental Health'}
                              </span>

                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  background: statusBg,
                                  color: statusColor
                                }}
                              >
                                {isApproved ? '✓ Live / Approved' : isRejected ? '✕ Rejected' : '⏳ Pending Review'}
                              </span>

                              <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                                📅 {dateStr}
                              </span>
                            </div>

                            <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#0f172a', fontWeight: 700 }}>
                              {blog.title}
                            </h4>

                            {blog.excerpt && (
                              <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: '1.4', maxWidth: '650px' }}>
                                {blog.excerpt}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            onClick={() => handleEditClick(blog)}
                            style={{
                              padding: '6px 12px',
                              background: '#ede9fe',
                              color: '#6d28d9',
                              border: '1px solid #ddd6fe',
                              borderRadius: '6px',
                              fontSize: '12.5px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            ✏️ Edit
                          </button>

                          <button
                            onClick={() => handleDeleteClick(blogId)}
                            style={{
                              padding: '6px 12px',
                              background: '#fee2e2',
                              color: '#b91c1c',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              fontSize: '12.5px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>

                      {/* Review Feedback Note if rejected or reviewed */}
                      {blog.reviewNote && (
                        <div style={{ padding: '8px 12px', background: isRejected ? '#fff1f2' : '#f0fdf4', borderRadius: '8px', border: `1px solid ${isRejected ? '#fecdd3' : '#bbf7d0'}`, fontSize: '12.5px', color: isRejected ? '#9f1239' : '#166534' }}>
                          <strong>💬 Admin Feedback / Review Note:</strong> {blog.reviewNote}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '12px' }}>
                <div style={{ fontSize: '36px', marginBottom: '8px' }}>📝</div>
                <h4 style={{ margin: '0 0 4px 0', color: '#334155', fontWeight: 700 }}>No articles found</h4>
                <p style={{ margin: 0, fontSize: '13px' }}>
                  Write and submit your first article using the form above to reach clients across the platform.
                </p>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default CreateBlogPage;
